import React, { useState } from "react";
import { Users, Lock, BarChart2, Crown, Eye, Search } from "lucide-react";
import {
  Usuario,
  CATEGORIA_LABELS,
  CategoriaProfissional,
} from "../../../types";
import UsuarioCard from "./UsuarioCard";
import { theme } from "../../../shared/styles/theme";

interface UsuariosListProps {
  usuarios: Usuario[];
  usuarioLogadoId: string;
  onEditar: (usuario: Usuario) => void;
  onExcluir: (id: string) => void;
  onBloquear: (id: string, bloquear: boolean) => void;
}

export default function UsuariosList({
  usuarios,
  usuarioLogadoId,
  onEditar,
  onExcluir,
  onBloquear,
}: UsuariosListProps) {
  const [termoBusca, setTermoBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState<string>("todos");
  const [filtroCategoria, setFiltroCategoria] = useState<string>("todos"); // ⭐ NOVO
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");

  // 🔍 FILTRAR USUÁRIOS
  const usuariosFiltrados = usuarios.filter((usuario) => {
    // Filtro de busca
    const termoBuscaLower = termoBusca.toLowerCase();
    const matchBusca =
      usuario.nome.toLowerCase().includes(termoBuscaLower) ||
      usuario.email.toLowerCase().includes(termoBuscaLower) ||
      usuario.registroProfissional?.numero
        ?.toLowerCase()
        .includes(termoBuscaLower) ||
      (usuario.categoria &&
        CATEGORIA_LABELS[usuario.categoria]
          ?.toLowerCase()
          .includes(termoBuscaLower));

    if (!matchBusca) return false;

    // Filtro de tipo
    if (filtroTipo !== "todos" && usuario.tipo !== filtroTipo) {
      return false;
    }

    // ⭐ NOVO: Filtro de categoria
    if (filtroCategoria !== "todos" && usuario.categoria !== filtroCategoria) {
      return false;
    }

    // Filtro de status
    if (filtroStatus === "bloqueados" && usuario.ativo) {
      return false;
    }
    if (filtroStatus === "ativos" && !usuario.ativo) {
      return false;
    }

    return true;
  });

  // 📊 ESTATÍSTICAS
  const stats = {
    total: usuarios.length,
    administradores: usuarios.filter((u) => u.tipo === "administrador").length,
    usuarios: usuarios.filter((u) => u.tipo === "usuario").length,
    convidados: usuarios.filter((u) => u.tipo === "convidado").length,
    bloqueados: usuarios.filter((u) => !u.ativo).length,

    // ⭐ NOVAS ESTATÍSTICAS POR CATEGORIA
    medicos: usuarios.filter((u) => u.categoria === "medico").length,
    enfermeiros: usuarios.filter((u) => u.categoria === "enfermeiro").length,
    psicologos: usuarios.filter((u) => u.categoria === "psicologo").length,
    nutricionistas: usuarios.filter((u) => u.categoria === "nutricionista")
      .length,
    farmaceuticos: usuarios.filter((u) => u.categoria === "farmaceutico")
      .length,
    administrativos: usuarios.filter((u) => u.categoria === "administrativo")
      .length,
  };

  return (
    <div>
      {/* ESTATÍSTICAS - TIPOS DE USUÁRIO */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "16px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "16px",
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.sm,
            border: `2px solid ${theme.gray[200]}`,
          }}
        >
          <p
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: theme.gray[800],
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            {stats.total}
          </p>
          <p
            style={{
              fontSize: "13px",
              color: theme.gray[600],
              margin: "4px 0 0 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            Total de Cadastrados
          </p>
        </div>

        <div
          style={{
            background: "white",
            padding: "16px",
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.sm,
            border: `2px solid ${theme.primary[200]}`,
          }}
        >
          <p
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: theme.primary[700],
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            {stats.administradores}
          </p>
          <p
            style={{
              fontSize: "13px",
              color: theme.gray[600],
              margin: "4px 0 0 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Crown size={16} style={{ marginRight: "6px" }} /> Administradores
          </p>
        </div>

        <div
          style={{
            background: "white",
            padding: "16px",
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.sm,
            border: "2px solid #d1fae5",
          }}
        >
          <p
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#10b981",
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            {stats.usuarios}
          </p>
          <p
            style={{
              fontSize: "13px",
              color: theme.gray[600],
              margin: "4px 0 0 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Users size={18} style={{ marginRight: "8px" }} /> Usuários
          </p>
        </div>

        <div
          style={{
            background: "white",
            padding: "16px",
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.sm,
            border: "2px solid #fef3c7",
          }}
        >
          <p
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#f59e0b",
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            {stats.convidados}
          </p>
          <p
            style={{
              fontSize: "13px",
              color: theme.gray[600],
              margin: "4px 0 0 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Eye size={16} style={{ marginRight: "6px" }} /> Convidados
          </p>
        </div>

        <div
          style={{
            background: "white",
            padding: "16px",
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.sm,
            border: "2px solid #fee2e2",
          }}
        >
          <p
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#dc2626",
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            {stats.bloqueados}
          </p>
          <p
            style={{
              fontSize: "13px",
              color: theme.gray[600],
              margin: "4px 0 0 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Lock size={18} style={{ marginRight: "8px" }} /> Bloqueados
          </p>
        </div>
      </div>

      {/* ⭐ NOVAS ESTATÍSTICAS - CATEGORIAS PROFISSIONAIS */}
      <div
        style={{
          background: `linear-gradient(135deg, ${theme.primary[50]} 0%, ${theme.primary[100]} 100%)`,
          padding: "20px",
          borderRadius: theme.borderRadius.lg,
          marginBottom: "24px",
          border: `1px solid ${theme.primary[200]}`,
        }}
      >
        <h4
          style={{
            fontSize: "16px",
            fontWeight: "bold",
            color: theme.primary[900],
            margin: "0 0 16px 0",
            fontFamily: theme.fonts.sans,
          }}
        >
          <BarChart2 size={16} style={{ marginRight: "8px" }} /> Por Categoria
          Profissional
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "12px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: theme.primary[700],
                margin: 0,
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              {stats.medicos}
            </p>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[700],
                margin: "4px 0 0 0",
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              Médicos
            </p>
          </div>
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: theme.primary[700],
                margin: 0,
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              {stats.enfermeiros}
            </p>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[700],
                margin: "4px 0 0 0",
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              Enfermeiros
            </p>
          </div>
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: theme.primary[700],
                margin: 0,
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              {stats.psicologos}
            </p>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[700],
                margin: "4px 0 0 0",
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              Psicólogos
            </p>
          </div>
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: theme.primary[700],
                margin: 0,
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              {stats.nutricionistas}
            </p>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[700],
                margin: "4px 0 0 0",
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              Nutricionistas
            </p>
          </div>
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: theme.primary[700],
                margin: 0,
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              {stats.farmaceuticos}
            </p>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[700],
                margin: "4px 0 0 0",
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              Farmacêuticos
            </p>
          </div>
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: theme.primary[700],
                margin: 0,
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              {stats.administrativos}
            </p>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[700],
                margin: "4px 0 0 0",
                fontFamily: theme.fonts.sans, // ⬅️ ADICIONAR
              }}
            >
              Administrativos
            </p>
          </div>
        </div>
      </div>

      {/* FILTROS */}
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.sm,
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr",
            gap: "12px",
          }}
        >
          {/* BUSCA */}
          <div>
            <input
              type="text"
              placeholder="Buscar por nome, email ou registro..."
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* FILTRO TIPO */}
          <div>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
                boxSizing: "border-box",
              }}
            >
              <option value="todos">Todos os tipos</option>
              <option value="administrador">Administradores</option>
              <option value="usuario">Usuários</option>
              <option value="convidado">Convidados</option>
            </select>
          </div>

          {/* ⭐ NOVO: FILTRO CATEGORIA */}
          <div>
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
                boxSizing: "border-box",
              }}
            >
              <option value="todos">Todas categorias</option>
              {(Object.keys(CATEGORIA_LABELS) as CategoriaProfissional[]).map(
                (cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORIA_LABELS[cat]}
                  </option>
                )
              )}
            </select>
          </div>

          {/* FILTRO STATUS */}
          <div>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
                boxSizing: "border-box",
              }}
            >
              <option value="todos">Todos os status</option>
              <option value="ativos">Ativos</option>
              <option value="bloqueados">Bloqueados</option>
            </select>
          </div>
        </div>
      </div>

      {/* LISTA DE USUÁRIOS */}
      {usuariosFiltrados.length === 0 ? (
        <div
          style={{
            background: "white",
            padding: "48px",
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.sm,
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: "16px",
              color: theme.gray[500],
              fontFamily: theme.fonts.sans,
            }}
          >
            {termoBusca ||
            filtroTipo !== "todos" ||
            filtroCategoria !== "todos" ||
            filtroStatus !== "todos"
              ? "Nenhum usuário encontrado com os filtros aplicados."
              : "Nenhum usuário cadastrado ainda."}
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
            gap: "20px",
          }}
        >
          {usuariosFiltrados.map((usuario) => (
            <UsuarioCard
              key={usuario.id}
              usuario={usuario}
              usuarioLogadoId={usuarioLogadoId}
              onEditar={onEditar}
              onExcluir={onExcluir}
              onBloquear={onBloquear}
            />
          ))}
        </div>
      )}
    </div>
  );
}
